import {createContext, useContext, useEffect, useMemo, useState} from "react";
import {HotKeys} from "react-hotkeys";

import {SessionContext} from './SessionContext';
import {useSelectedAssetsState} from './SelectedAssetsContext';
import {useAssetsState} from './AssetsContext';

const BulkContext = createContext();

const AssetActionsProvider = ({children})=>{
    const {state: {currentOrg}} = useContext(SessionContext)
    const {selectedAssetIds} = useSelectedAssetsState()
    const {assets} = useAssetsState()
    const selectedTextAssetId = currentOrg?.configurable_entities_enabled && selectedAssetIds.length === 1 &&
        assets[selectedAssetIds[0]]?.entity_type ? selectedAssetIds[0] : null
    const [textAssetFormRequest, setTextAssetFormRequest] = useState(null)
    const textAssetFormOpen = !!selectedTextAssetId && textAssetFormRequest?.assetId === selectedTextAssetId &&
        textAssetFormRequest?.organizationId === currentOrg?.id

    // A new selection or organization requires another explicit menu action.
    useEffect(() => {
        if (!textAssetFormOpen) setTextAssetFormRequest(null)
    }, [textAssetFormOpen])

    const useWrappedState = ()=>{
        const [object, setObject] = useState()

        const setObjectAndResetFocus = value =>{
            setObject(value)
        }

        return [object, setObjectAndResetFocus]
    }

    const [downloadModalOpen, setDownloadModalOpen] = useWrappedState()
    const [downloadAsset, setDownloadAsset] = useState()
    const [downloadStorageFolder, setDownloadStorageFolder] = useState()
    const [shareModalOpen, setShareModalOpen] = useWrappedState()

    const [editModalOpen, setEditModalOpen] = useWrappedState()
    const [rightsModalOpen, setRightsModalOpen] = useWrappedState()
    const [releasesModalOpen, setReleasesModalOpen] = useWrappedState()

    const [destroyModalOpen, setDestroyModalOpen] = useWrappedState()
    const [restoreModalOpen, setRestoreModalOpen] = useWrappedState()

    const [addToLightboxModalOpen, setAddToLightboxModalOpen] = useWrappedState()
    const [addToCollectionModalOpen, setAddToCollectionModalOpen] = useWrappedState()

    const [accessRequestModalOpen, setAccessRequestModalOpen] = useWrappedState()
    const [requestAssetIds, setRequestAssetIds] = useState()
    
    const [accessGrantModalOpen, setAccessGrantModalOpen] = useWrappedState()
    const [grantAssetIds, setGrantAssetIds] = useState()

    const keyMap = {
        CMD_E: 'command+e',
        CMD_SHIFT_S: 'command+shift+s',
        CMD_D: 'command+backspace',
        CMD_OPT_S: 'command+option+s',
    }

    const handlers = {
        CMD_E: ()=> { setEditModalOpen(true) },
        CMD_SHIFT_S: (e)=> {
            e.preventDefault()
            setDownloadModalOpen(true)
        },
        CMD_D: e=> {
            e.preventDefault()
            setDestroyModalOpen(true)
        },
        CMD_OPT_S: ()=> { setShareModalOpen(true) },
    }

    const contextValue = useMemo(() => ({
        selectedTextAssetId, textAssetFormOpen, textAssetFormRequest,
        openTextAssetForm: () => {
            if (selectedTextAssetId) setTextAssetFormRequest({assetId: selectedTextAssetId, organizationId: currentOrg.id})
        },
        closeTextAssetForm: () => setTextAssetFormRequest(null),
        downloadModalOpen, setDownloadModalOpen,
        downloadAsset, setDownloadAsset,
        downloadStorageFolder, setDownloadStorageFolder,

        accessRequestModalOpen, setAccessRequestModalOpen,
        requestAssetIds, setRequestAssetIds,

        accessGrantModalOpen, setAccessGrantModalOpen,
        grantAssetIds, setGrantAssetIds,

        editModalOpen, setEditModalOpen,
        destroyModalOpen, setDestroyModalOpen,
        shareModalOpen, setShareModalOpen,
        restoreModalOpen, setRestoreModalOpen,
        rightsModalOpen, setRightsModalOpen,
        releasesModalOpen, setReleasesModalOpen,
        addToLightboxModalOpen, setAddToLightboxModalOpen,
        addToCollectionModalOpen, setAddToCollectionModalOpen
    }), [
        selectedTextAssetId, textAssetFormOpen, textAssetFormRequest, currentOrg?.id,
        downloadModalOpen, downloadAsset, downloadStorageFolder,
        accessRequestModalOpen, requestAssetIds,
        accessGrantModalOpen, grantAssetIds,
        editModalOpen, destroyModalOpen, shareModalOpen,
        restoreModalOpen, rightsModalOpen, releasesModalOpen,
        addToLightboxModalOpen, addToCollectionModalOpen
    ])

    return (
        <BulkContext.Provider value={contextValue}>
            <HotKeys keyMap={keyMap} handlers={handlers}>
                {children}
            </HotKeys>
        </BulkContext.Provider>
    )
}

export default AssetActionsProvider

const useAssetActions = ()=> {
    return useContext(BulkContext)
}

export {useAssetActions}